import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import SydButton from '../../../../commonDesign/Button'
import { AgentPanelProvider } from './AgentPanelContext'
import ChatPanel from './chat/ChatPanel'

const useStyles = makeStyles(() => ({
  panel: {
    top: '80px',
    right: 0,
    bottom: 0,
    width: '400px',
    height: 'auto',
    background: 'white',
    zIndex: 1000,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
    opacity: 0,
    '&.slide-in': {
      transform: 'translateX(0)',
      opacity: 1
    }
  },
  closeButton: {
    top: '6px',
    right: '100%',
    marginRight: '6px',
    zIndex: 1001
  }
}))

/**
 * Slide-in side panel for AI agent interactions.
 * Wraps content in AgentPanelProvider so child components
 * can access agent state via useAgentPanelContext.
 *
 * @param {string} agentId - Which agent to use (e.g. 'ae-view-builder', 'ae-view-editor')
 * @param {Function} onClose - Called when the panel is dismissed
 * @param {Function} [onJsxUpdate] - Called when user applies generated JSX
 * @param {Function} [onAttachmentLoadStart] - Called when an attachment starts loading
 * @param {Function} [onVersionCreated] - Called when a new version is created
 * @param {Function} [onThreadIdChange] - Called when a new thread is created
 * @param {string} [initialThreadId] - Resume an existing thread
 * @param {Object} [viewContext] - View context for agents that need it ({ viewId, content })
 */
function AgentSidePanel ({
  agentId,
  onClose,
  onJsxUpdate,
  onAttachmentLoadStart,
  onThreadIdChange,
  onVersionCreated,
  initialThreadId,
  viewContext
}) {
  const classes = useStyles()
  const containerRef = useRef(null)
  const [isSlideIn, setIsSlideIn] = useState(false)

  useLayoutEffect(() => {
    const timer = setTimeout(() => setIsSlideIn(true), 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onWheel = e => { if (e.deltaX !== 0) e.preventDefault() }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  const handleApplyJsx = (jsxContent) => {
    if (onAttachmentLoadStart) onAttachmentLoadStart()
    if (onJsxUpdate) onJsxUpdate(jsxContent)
  }

  const handleClose = () => {
    setIsSlideIn(false)
    setTimeout(() => onClose(), 300)
  }

  return (
    <AgentPanelProvider
      agentId={agentId}
      initialThreadId={initialThreadId}
      viewContext={viewContext}
      onApplyJsx={handleApplyJsx}
      onThreadIdChange={onThreadIdChange}
      onVersionCreated={onVersionCreated}
    >
      <div ref={containerRef} className={`${classes.panel} absolute flex flex-col ${isSlideIn ? 'slide-in' : ''}`}>
        <div className={`${classes.closeButton} absolute`}>
          <SydButton icon='close' size='sm' variant='ai' onClick={handleClose}>
            Close
          </SydButton>
        </div>
        <ChatPanel />
      </div>
    </AgentPanelProvider>
  )
}

AgentSidePanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onJsxUpdate: PropTypes.func,
  onAttachmentLoadStart: PropTypes.func,
  onThreadIdChange: PropTypes.func,
  onVersionCreated: PropTypes.func,
  initialThreadId: PropTypes.string,
  viewContext: PropTypes.shape({
    viewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string
  })
}

export default AgentSidePanel
